'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Card, CardContent } from '@/components/ui/card';
import { MessageCircle } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

export default function FAQSection() {
    const { t, isLoading } = useTranslation('root-canal');
    const { isRTL } = useRTL();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLDivElement>(null);
    const accordionRef = useRef<HTMLDivElement>(null);
    const helpRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!sectionRef.current) return;

        const ctx = gsap.context(() => {
            // Title animation
            if (titleRef.current) {
                gsap.fromTo(titleRef.current,
                    { opacity: 0, y: 50 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 1,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: titleRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Accordion items animation
            if (accordionRef.current?.children) {
                gsap.fromTo(Array.from(accordionRef.current.children),
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.6,
                        stagger: 0.1,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: accordionRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

            // Help section animation
            if (helpRef.current) {
                gsap.fromTo(helpRef.current,
                    { opacity: 0, y: 30 },
                    {
                        opacity: 1,
                        y: 0,
                        duration: 0.8,
                        ease: "power2.out",
                        scrollTrigger: {
                            trigger: helpRef.current,
                            start: "top 85%",
                            toggleActions: "play none none reverse"
                        }
                    }
                );
            }

        }, sectionRef);

        return () => {
            ctx.revert();
        };
    }, []);

    const faqs = [
        {
            question: t('faq.questions.painful.question'),
            answer: t('faq.questions.painful.answer')
        },
        {
            question: t('faq.questions.crown.question'),
            answer: t('faq.questions.crown.answer')
        },
        {
            question: t('faq.questions.duration.question'),
            answer: t('faq.questions.duration.answer')
        },
        {
            question: t('faq.questions.delay.question'),
            answer: t('faq.questions.delay.answer')
        },
        {
            question: t('faq.questions.travel.question'),
            answer: t('faq.questions.travel.answer')
        }
    ];

    return (
        <section id="faq" className="py-16 lg:py-20 bg-slate-50 dark:bg-navy-950 min-h-[600px]">
            <div className="container">
                <div className="text-center mb-12">
                    <h2 ref={titleRef} className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                        {t('faq.title.part1')} <span className="text-gold">{t('faq.title.part2')}</span>
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t('faq.subtitle')}
                    </p>
                </div>

                <div className="max-w-4xl mx-auto">
                    <div ref={accordionRef}>
                        <Accordion type="single" collapsible className="space-y-4">
                            {faqs.map((faq, index) => (
                                <AccordionItem key={index} value={`item-${index}`} className="border-0">
                                    <Card className="border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                                        <AccordionTrigger className={`px-8 py-6 ${isRTL ? 'text-right' : 'text-left'} hover:no-underline group`}>
                                            <h4 className="text-lg font-semibold text-navy dark:text-white group-hover:text-gold transition-colors">
                                                {faq.question}
                                            </h4>
                                        </AccordionTrigger>
                                        <AccordionContent className="px-8 pb-6">
                                            <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
                                                <p className={`text-slate-600 dark:text-slate-300 leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                                                    {faq.answer}
                                                </p>
                                            </div>
                                        </AccordionContent>
                                    </Card>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>

                    {/* Additional Help */}
                    <div ref={helpRef} className="text-center mt-12">
                        <Card className="bg-gold/10 border-gold/30">
                            <CardContent className="p-8">
                                <h3 className="text-xl font-bold text-navy dark:text-white mb-4">
                                    {t('faq.help.title')}
                                </h3>
                                <p className={`text-slate-600 dark:text-slate-300 mb-6 ${isRTL ? 'text-right' : 'text-center'}`}>
                                    {t('faq.help.description')}
                                </p>
                                <button
                                    onClick={() => window.open(`https://wa.me/905070808888?text=${encodeURIComponent(t('faq.help.whatsappMessage'))}`, '_blank')}
                                    className={`bg-gold hover:bg-gold-600 text-navy font-semibold px-8 py-4 rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 mx-auto ${isRTL ? 'flex-row-reverse' : ''}`}
                                >
                                    <MessageCircle className="w-5 h-5" />
                                    {t('faq.help.button')}
                                </button>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </section>
    );
} 